import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG } from '../../lagerplatz-umlagerung.shared';
import { LagerplatzUmlagerungStore } from '../../store';
import { Store } from '@softline/core';
import { Router } from '@angular/router';
import {
  BackNavigationService,
  BlockingLoadingSpinnerComponent,
  showRequestErrors,
  HeaderComponent,
  ScannerModule,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { BehaviorSubject } from 'rxjs';
import { MessageBarStore, SOFTLINE_FEATURE_MESSAGE_BAR, TranslatePipe } from '@softline/ui-core';
import { type ProgressStep, ProgressStepsComponent } from '@softline/ui-core/progress';
import {
  Lagerplatz,
  LagerplatzInhalt,
  LagerplatzSucheComponent,
} from '@softapps/lager/core';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';

@Component({
  selector: 'soft-ziellagerplatz-scannen',
  imports: [
    CommonModule,
    ScannerModule,
    ProgressStepsComponent,
    BlockingLoadingSpinnerComponent,
    LagerplatzSucheComponent,
    HeaderComponent,
    TranslatePipe,
    PageComponent,
    PageHeaderComponent,
  ],
  templateUrl: './ziellagerplatz-scannen.page.html',
  styleUrls: ['./ziellagerplatz-scannen.page.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZiellagerplatzScannenPage implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly backNavigationService = inject(BackNavigationService);

  readonly steps: ProgressStep[] = [
    {
      header: '#LAGERPLATZ_UMLAGERUNG.PAGES.QUELLLAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#LAGERPLATZ_UMLAGERUNG.PAGES.QUELLLAGERPLATZ_SCANNEN.DESCRIPTION',
    },
    {
      header: '#LAGERPLATZ_UMLAGERUNG.PAGES.ARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERPLATZ_UMLAGERUNG.PAGES.ARTIKEL_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERPLATZ_UMLAGERUNG.PAGES.MENGEN_AUSWAHL.TITLE',
      subHeader: '#LAGERPLATZ_UMLAGERUNG.PAGES.MENGEN_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERPLATZ_UMLAGERUNG.PAGES.ZIELLAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#LAGERPLATZ_UMLAGERUNG.PAGES.ZIELLAGERPLATZ_SCANNEN.DESCRIPTION',
    },
  ];

  loading$ = new BehaviorSubject<boolean>(false);

  readonly quellLagerplatz = signal<LagerplatzInhalt | null>(null);

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#LAGERPLATZ_UMLAGERUNG.TITLE'
    );
    const quellLagerplatz = this.store.get(
      SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG,
      LagerplatzUmlagerungStore.getters.quellLagerplatz
    );

    this.quellLagerplatz.set(quellLagerplatz);
  }

  ngOnDestroy() {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/lagerplatz-umlagerung', 'mengen-auswahl']);
  }

  async onLagerplatzSelected(zielLagerplatz: Lagerplatz): Promise<void> {
    if (!zielLagerplatz) return;

    this.loading$.next(true);
    try {
      const quellLagerplatz = this.store.get(
        SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG,
        LagerplatzUmlagerungStore.getters.quellLagerplatz
      );
      const artikelMenge = this.store.get(
        SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG,
        LagerplatzUmlagerungStore.getters.artikel
      );
      const palette =
        this.store.get(
          SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG,
          LagerplatzUmlagerungStore.getters.mischPalette
        )?.palette ?? null;
      await this.store.dispatch(
        SOFTLINE_FEATURE_LAGERPLATZ_UMLAGERUNG,
        LagerplatzUmlagerungStore.actions.umlagern,
        {
          artikelMenge: artikelMenge ? [artikelMenge] : null,
          palette,
          quellLagerplatz: quellLagerplatz.lagerplatz,
          zielLagerplatz,
        }
      );
      this.loading$.next(false);
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        '#LAGERPLATZ_UMLAGERUNG.MESSAGES.SUCCESS.UMLAGERUNG'
      );
      await this.router.navigate(['/lagerplatz-umlagerung']);
    } catch (e) {
      this.loading$.next(false);
      showRequestErrors(this.store, e);
    }
  }
}
